<template>
  <div class="beschreibungsfeld">
    <div class="beschreibungsfeld-kopf">
      <label>{{ label }}</label>
      <div class="beschreibungsfeld-aktionen">
        <button type="button" class="beschreibungsfeld-btn" @click="EditorOeffnen">
          <font-awesome-icon icon="fa-solid fa-pen" />
          Formatiert bearbeiten
        </button>
        <button
          type="button"
          class="beschreibungsfeld-btn"
          :disabled="IstLeer"
          @click="AnsichtOeffnen"
        >
          <font-awesome-icon icon="fa-solid fa-eye" />
          Komfortabel anzeigen
        </button>
      </div>
    </div>

    <div
      class="beschreibung-vorschau"
      :class="{ 'beschreibung-vorschau--leer': IstLeer }"
      role="button"
      tabindex="0"
      @click="EditorOeffnen"
      @keydown.enter="EditorOeffnen"
    >
      <div v-if="!IstLeer" class="beschreibung-vorschau-inhalt beschreibung-vorschau-inhalt--geklemmt" v-html="AnsichtHtml"></div>
      <span v-else class="beschreibung-vorschau-platzhalter">{{ placeholder }}</span>
    </div>

    <!-- Formatiert bearbeiten (Rich-Text-Editor) -->
    <Teleport to="body">
      <div v-if="EditorOffen" class="modal-overlay" @click.self="EditorSchliessen">
        <div class="modal-sheet editor-sheet">
          <div class="modal-kopf">
            <span class="modal-titel">Formatiert bearbeiten</span>
            <button class="modal-schliessen" @click="EditorSchliessen" aria-label="Schließen">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="editor-toolbar">
            <button type="button" class="editor-toolbar-btn" title="Fett" @mousedown.prevent @click="Formatieren('bold')">
              <font-awesome-icon icon="fa-solid fa-bold" />
            </button>
            <button type="button" class="editor-toolbar-btn" title="Kursiv" @mousedown.prevent @click="Formatieren('italic')">
              <font-awesome-icon icon="fa-solid fa-italic" />
            </button>
            <button type="button" class="editor-toolbar-btn" title="Unterstrichen" @mousedown.prevent @click="Formatieren('underline')">
              <font-awesome-icon icon="fa-solid fa-underline" />
            </button>
            <span class="editor-trenner"></span>
            <button type="button" class="editor-toolbar-btn" title="Überschrift" @mousedown.prevent @click="UeberschriftToggeln">
              <font-awesome-icon icon="fa-solid fa-heading" />
            </button>
            <button type="button" class="editor-toolbar-btn" title="Aufzählung" @mousedown.prevent @click="Formatieren('insertUnorderedList')">
              <font-awesome-icon icon="fa-solid fa-list-ul" />
            </button>
            <button type="button" class="editor-toolbar-btn" title="Nummerierte Liste" @mousedown.prevent @click="Formatieren('insertOrderedList')">
              <font-awesome-icon icon="fa-solid fa-list-ol" />
            </button>
            <span class="editor-trenner"></span>
            <button type="button" class="editor-toolbar-btn" title="Rückgängig" @mousedown.prevent @click="Formatieren('undo')">
              <font-awesome-icon icon="fa-solid fa-rotate-left" />
            </button>
          </div>

          <div
            ref="EditorFeld"
            class="editor-inhalt"
            contenteditable="true"
            :data-placeholder="placeholder"
          ></div>

          <div class="modal-fuss">
            <button type="button" class="btn btn-primary btn-breit" @click="EditorUebernehmen">Übernehmen</button>
            <button type="button" class="btn btn-secondary btn-breit" @click="EditorSchliessen">Abbrechen</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Komfortabel anzeigen (Kochmodus) -->
    <Teleport to="body">
      <div v-if="AnsichtOffen" class="ansicht-overlay" @click.self="AnsichtSchliessen">
        <div class="ansicht-karte">
          <button class="ansicht-schliessen" @click="AnsichtSchliessen" aria-label="Schließen">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
          <div class="ansicht-inhalt" v-html="AnsichtHtml"></div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
// Alte, rein textbasierte Beschreibungen (vor diesem Feature) enthalten kein HTML –
// beim Öffnen des Editors werden ihre Zeilenumbrüche einmalig in <br> umgewandelt,
// damit sie im contenteditable-Feld weiterhin mehrzeilig erscheinen.
function TextZuHtml(Text) {
  if (!Text) return ''
  // Nur Tags erkennen, die der Editor selbst erzeugt - sonst wuerde z.B. der reine
  // Klartext "<Suppe>" faelschlich als bereits vorhandenes HTML durchgereicht und
  // vom Browser als unbekanntes Element verschluckt (unsichtbar) statt escaped.
  const HatTags = /<\/?(b|i|u|strong|em|ul|ol|li|h[1-6]|div|br|p|span)(\s|>|\/)/i.test(Text)
  if (HatTags) return Text
  const Escaped = Text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return Escaped.replace(/\n/g, '<br>')
}

export default {
  name: 'BeschreibungsFeld',
  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, default: 'Beschreibung' },
    placeholder: { type: String, default: 'Zubereitung, Hinweise, ...' }
  },
  emits: ['update:modelValue', 'uebernommen'],
  data() {
    return {
      EditorOffen: false,
      AnsichtOffen: false
    }
  },
  computed: {
    IstLeer() {
      const Reiner = (this.modelValue || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
      return Reiner === ''
    },
    AnsichtHtml() {
      return TextZuHtml(this.modelValue)
    }
  },
  methods: {
    EditorOeffnen() {
      this.EditorOffen = true
      this.$nextTick(() => {
        if (this.$refs.EditorFeld) {
          this.$refs.EditorFeld.innerHTML = TextZuHtml(this.modelValue)
          this.$refs.EditorFeld.focus()
        }
      })
    },
    EditorSchliessen() {
      this.EditorOffen = false
    },
    EditorUebernehmen() {
      const Html = this.$refs.EditorFeld ? this.$refs.EditorFeld.innerHTML : ''
      const NurLeer = Html.replace(/<br\s*\/?>/gi, '').replace(/<[^>]+>/g, '').trim() === ''
      const NeuerWert = NurLeer ? '' : Html
      this.$emit('update:modelValue', NeuerWert)
      // Eigenes Event, damit die Elternseite (falls das Rezept schon existiert)
      // die Beschreibung sofort persistieren kann, statt auf den separaten
      // Speichern-Button der Gesamtseite zu warten.
      this.$emit('uebernommen', NeuerWert)
      this.EditorOffen = false
    },
    Formatieren(Befehl, Wert = null) {
      if (this.$refs.EditorFeld) this.$refs.EditorFeld.focus()
      document.execCommand(Befehl, false, Wert)
    },
    UeberschriftToggeln() {
      const Aktuell = (document.queryCommandValue('formatBlock') || '').toLowerCase()
      this.Formatieren('formatBlock', Aktuell === 'h3' ? 'p' : 'h3')
    },
    AnsichtOeffnen() {
      if (this.IstLeer) return
      this.AnsichtOffen = true
    },
    AnsichtSchliessen() {
      this.AnsichtOffen = false
    }
  }
}
</script>

<style scoped>
.beschreibungsfeld {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
}

.beschreibungsfeld-kopf {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.beschreibungsfeld-kopf label {
  font-size: 0.9rem;
}

.beschreibungsfeld-aktionen {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.beschreibungsfeld-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg-alt);
  color: var(--color-primary);
  font-size: 0.8rem;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.beschreibungsfeld-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
}

.beschreibungsfeld-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.beschreibung-vorschau {
  min-height: 44px;
  max-height: 92px;
  overflow: hidden;
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.beschreibung-vorschau:hover {
  border-color: var(--color-primary);
}

.beschreibung-vorschau--leer {
  display: flex;
  align-items: center;
}

.beschreibung-vorschau-platzhalter {
  color: var(--color-text-muted);
}

/* Vorschau ist nur ein Einstiegspunkt zum Editor bzw. zur "Komfortabel anzeigen"-
   Ansicht - laengere Beschreibungen werden hier bewusst abgeschnitten, damit das
   Feld in der normalen Formularansicht kompakt bleibt. */
.beschreibung-vorschau-inhalt--geklemmt {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.beschreibung-vorschau-inhalt :deep(h3) {
  margin: 0 0 6px;
  font-size: 1.1rem;
}

.beschreibung-vorschau-inhalt :deep(ul),
.beschreibung-vorschau-inhalt :deep(ol) {
  padding-left: 1.3em;
  margin: 6px 0;
}

.beschreibung-vorschau-inhalt :deep(p) {
  margin: 0 0 8px;
}

.beschreibung-vorschau-inhalt :deep(p):last-child {
  margin-bottom: 0;
}

/* ── Modal (gleiche Optik wie die Zutaten-/Kategorie-Sheets der Rezept-Seiten) ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  animation: Einblenden 0.25s ease both;
}

.modal-sheet {
  width: 100%;
  max-height: 90dvh;
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .modal-overlay {
    align-items: center;
    justify-content: center;
  }
  .modal-sheet {
    border-radius: var(--radius-xl);
    max-width: 620px;
    max-height: 84dvh;
  }
}

.modal-kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.modal-titel {
  font-size: 1.1rem;
  font-weight: 600;
}

.modal-schliessen {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-fuss {
  padding: 16px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-breit {
  width: 100%;
}

/* ── Editor-Toolbar ──────────────────────────────────── */
.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.editor-toolbar-btn {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-alt);
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.editor-toolbar-btn:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.editor-trenner {
  width: 1px;
  align-self: stretch;
  margin: 4px 4px;
  background: var(--color-border);
}

.editor-inhalt {
  flex: 1;
  min-height: 240px;
  padding: 16px 20px;
  overflow-y: auto;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text);
  outline: none;
}

.editor-inhalt:empty:before {
  content: attr(data-placeholder);
  color: var(--color-text-muted);
  pointer-events: none;
}

.editor-inhalt :deep(h3) {
  margin: 14px 0 6px;
  font-size: 1.15rem;
}

.editor-inhalt :deep(ul),
.editor-inhalt :deep(ol) {
  padding-left: 1.4em;
  margin: 8px 0;
}

/* ── Komfortabel anzeigen (Kochmodus) ────────────────── */
.ansicht-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.55);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  z-index: 250;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: Einblenden 0.2s ease both;
}

.ansicht-karte {
  position: relative;
  width: 100%;
  max-width: 640px;
  max-height: 88dvh;
  overflow-y: auto;
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl);
  box-shadow: var(--glas-schatten);
  padding: 40px 28px 32px;
}

.ansicht-schliessen {
  position: sticky;
  top: -8px;
  float: right;
  margin: -28px -16px 8px 8px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--glas-schatten);
}

.ansicht-inhalt {
  font-size: 1.15rem;
  line-height: 1.75;
  color: var(--color-text);
  white-space: pre-line;
}

.ansicht-inhalt :deep(h3) {
  margin: 20px 0 10px;
  font-size: 1.3rem;
}

.ansicht-inhalt :deep(ul),
.ansicht-inhalt :deep(ol) {
  padding-left: 1.4em;
  margin: 10px 0;
}

.ansicht-inhalt :deep(p) {
  margin: 0 0 12px;
}
</style>
